import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Briefcase,
  MapPin,
  AlertCircle,
  Check,
  X,
  Camera,
  Plus,
  AlertTriangle,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface Job {
  id: number;
  booking_id: number;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  service: string;
  service_category?: string;
  address: string;
  postcode: string;
  booking_date: string;
  booking_time_start: string;
  estimated_duration: string;
  total_amount: string;
  notes: string;
  customer_notes: string;
  status: 'pending' | 'accepted' | 'in_progress' | 'completed' | 'cancelled' | 'rejected';
  created_at: string;
  sla_minutes: number;
  accepted_at?: string;
  started_at?: string;
  completed_at?: string;
}

const ProviderAvailableJobsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchJobs();
    }
  }, [user?.id, authLoading]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/jobs?status=pending`);
      let jobsToDisplay = [];
      if (response?.data?.jobs && Array.isArray(response.data.jobs)) {
        jobsToDisplay = response.data.jobs;
      } else if (response?.data?.pending && Array.isArray(response.data.pending)) {
        jobsToDisplay = response.data.pending;
      } else if (Array.isArray(response?.data)) {
        jobsToDisplay = response.data;
      }
      setJobs(jobsToDisplay);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load jobs',
        variant: 'destructive',
      });
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptJob = async (jobId: number) => {
    try {
      await apiService.post(`/provider/jobs/${jobId}/accept`);
      toast({ title: 'Success', description: 'Job accepted' });
      fetchJobs();
    } catch {
      toast({ title: 'Error', description: 'Failed to accept job', variant: 'destructive' });
    }
  };

  const handleDeclineJob = async (jobId: number) => {
    try {
      await apiService.post(`/provider/jobs/${jobId}/reject`, {});
      toast({ title: 'Job declined', description: 'The job has been declined' });
      fetchJobs();
    } catch {
      toast({ title: 'Error', description: 'Failed to decline job', variant: 'destructive' });
    }
  };

  const handleUpdateStatus = async (bookingId: number, status: string) => {
    try {
      await apiService.patch(`/provider/bookings/${bookingId}`, { status });
      toast({
        title: 'Success',
        description: 'Status updated',
      });
      fetchJobs();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  // 🔧 FIXED: Safe formatting functions to prevent NaN/Invalid Date display
  const formatAmountSafe = (amount: any) => {
    if (amount === null || amount === undefined || amount === '') return '0.00';
    const num = Number(amount);
    if (isNaN(num) || !isFinite(num)) return '0.00';
    // 🔧 CRITICAL FIX: Divide by 100 to convert pence to pounds
    return (num / 100).toFixed(2);
  };

  const formatDateSafe = (dateStr: any) => {
    if (!dateStr) return 'Date TBD';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Date TBD';
      // Check for epoch (01/01/1970)
      if (date.getFullYear() === 1970 && date.getMonth() === 0 && date.getDate() === 1) {
        return 'Date TBD';
      }
      return date.toLocaleDateString('en-GB');
    } catch (e) {
      return 'Date TBD';
    }
  };

  const calculateSlaProgress = (job: Job) => {
    if (job.status !== 'pending') return 100;
    
    const validSlaMinutes = job.sla_minutes ? Number(job.sla_minutes) : 60;
    if (isNaN(validSlaMinutes) || !isFinite(validSlaMinutes)) return 0;
    
    try {
      const created = new Date(job.created_at).getTime();
      const now = new Date().getTime();
      const elapsed = (now - created) / (1000 * 60);
      const progress = (elapsed / validSlaMinutes) * 100;
      
      return Math.min(Math.max(progress, 0), 100);
    } catch (e) {
      return 0;
    }
  };

  const getTimeRemaining = (job: Job) => {
    if (job.status !== 'pending') return 'Responded';
    
    const validSlaMinutes = job.sla_minutes ? Number(job.sla_minutes) : 60;
    if (isNaN(validSlaMinutes) || !isFinite(validSlaMinutes)) return 'N/A';
    
    // 🔧 FIXED: Add validation for created_at before parsing
    if (!job.created_at) return 'N/A';
    
    try {
      const createdDate = new Date(job.created_at);
      const createdTime = createdDate.getTime();
      
      // 🔧 FIXED: Check if date parsing was successful
      if (isNaN(createdTime)) return 'N/A';
      
      const now = new Date().getTime();
      const elapsed = (now - createdTime) / (1000 * 60);
      const remaining = validSlaMinutes - elapsed;
      
      // 🔧 FIXED: Validate remaining calculation
      if (!isFinite(remaining)) return 'N/A';
      if (remaining <= 0) return 'SLA Expired';
      if (remaining < 60) return `${Math.ceil(remaining)} minutes left`;
      
      const hours = Math.floor(remaining / 60);
      const minutes = Math.ceil(remaining % 60);
      
      // 🔧 FIXED: Validate hours and minutes are valid numbers
      if (isNaN(hours) || isNaN(minutes)) return 'N/A';
      
      return `${hours}h ${minutes}m left`;
    } catch (e) {
      console.error('Error calculating time remaining:', e);
      return 'N/A';
    }
  };

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Authenticating...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  const filteredJobs = jobs;

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'plumbing': 'bg-blue-100 text-blue-700',
      'electrical': 'bg-yellow-100 text-yellow-700',
      'hvac': 'bg-cyan-100 text-cyan-700',
      'general': 'bg-gray-100 text-gray-700',
      'heating': 'bg-red-100 text-red-700',
      'maintenance': 'bg-purple-100 text-purple-700',
    };
    return colors[category?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  return (
    <ProviderLayout>
      <Helmet>
        <title>Available Jobs - VireWire Provider</title>
        <meta name="description" content="Browse and accept available jobs in your service area" />
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Available Jobs</h1>
              <p className="text-gray-600 mt-1">Browse and accept jobs in your service area</p>
            </div>
            <Button className="bg-green-600 hover:bg-green-700 text-white gap-2">
              <Plus className="w-4 h-4" />
              Set Availability
            </Button>
          </div>
        </div>

        {/* Info Bar */}
        {jobs.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-900 font-semibold">
              📌 {jobs.length} job{jobs.length !== 1 ? 's' : ''} available in your area
            </p>
            <p className="text-blue-700 text-sm mt-1">Review and accept jobs below</p>
          </div>
        )}

        {/* Jobs List */}
        {loading ? (
          <Card>
            <CardContent className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </CardContent>
          </Card>
        ) : filteredJobs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No jobs available in your service area at the moment</p>
              <p className="text-gray-500 text-sm mt-2">Set your availability and service preferences to get more jobs</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4">
              {filteredJobs.map((job) => {
                const timeRemaining = getTimeRemaining(job);
                return (
                  <Card key={job.id} className="border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/provider/job/${job.id}`)}>
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        {/* Top Row: Category & Status */}
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <Badge className={getCategoryColor(job.service_category)}>
                              {job.service_category || 'General'}
                            </Badge>
                            {job.status === 'pending' && (
                              <Badge variant="outline" className="border-yellow-300 bg-yellow-50 text-yellow-700">
                                ⏱️ Urgent
                              </Badge>
                            )}
                          </div>
                          <Badge 
                            className={
                              job.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              job.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                              job.status === 'in_progress' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }
                          >
                            {job.status === 'pending' ? 'Awaiting Response' :
                             job.status === 'confirmed' ? 'Accepted' :
                             job.status === 'in_progress' ? 'In Progress' : 
                             'Completed'}
                          </Badge>
                        </div>

                        {/* Job Title & Location */}
                        <div>
                          {job.service && <h3 className="text-lg font-bold text-gray-900">{job.service}</h3>}
                          {(job.address || job.postcode) && (
                            <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                              <MapPin className="w-4 h-4" />
                              {job.address && <span>{job.address}</span>}
                              {job.address && job.postcode && <span>•</span>}
                              {job.postcode && <span>{job.postcode}</span>}
                            </div>
                          )}
                        </div>

                        {/* Price - Prominent Display */}
                        <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-l-green-600">
                          <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Quote Amount</p>
                          <p className="text-3xl font-bold text-gray-900">£{formatAmountSafe(job.total_amount)}</p>
                        </div>

                        {/* Details Grid - only show fields with data */}
                        {(job.booking_date || job.estimated_duration || job.customer_name || job.customer_phone) && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-t border-b text-sm">
                            {job.booking_date && (
                              <div>
                                <p className="text-gray-600 text-xs uppercase font-semibold mb-1">📅 Date</p>
                                <p className="font-medium">{formatDateSafe(job.booking_date)}</p>
                                {job.booking_time_start && <p className="text-xs text-gray-500">{job.booking_time_start}</p>}
                              </div>
                            )}
                            {job.estimated_duration && (
                              <div>
                                <p className="text-gray-600 text-xs uppercase font-semibold mb-1">⏱️ Duration</p>
                                <p className="font-medium">{job.estimated_duration}</p>
                                <p className="text-xs text-gray-500">estimated</p>
                              </div>
                            )}
                            {job.customer_name && (
                              <div>
                                <p className="text-gray-600 text-xs uppercase font-semibold mb-1">👤 Customer</p>
                                <p className="font-medium text-sm">{job.customer_name}</p>
                              </div>
                            )}
                            {job.customer_phone && (
                              <div>
                                <p className="text-gray-600 text-xs uppercase font-semibold mb-1">📞 Contact</p>
                                <p className="font-medium text-sm">{job.customer_phone}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* SLA Indicator for Pending Jobs */}
                        {job.status === 'pending' && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-yellow-700">SLA Response Time</span>
                              <span className="text-xs font-bold text-yellow-700">{timeRemaining}</span>
                            </div>
                            <Progress value={calculateSlaProgress(job)} className="h-1.5" />
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-2">
                          {job.status === 'pending' ? (
                            <>
                              <Button 
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAcceptJob(job.id);
                                }}
                              >
                                <Check className="w-4 h-4" />
                                Accept Job
                              </Button>
                              <Button 
                                variant="outline" 
                                className="flex-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Counter-Offer
                              </Button>
                              <Button
                                variant="outline"
                                className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeclineJob(job.id);
                                }}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Decline
                              </Button>
                            </>
                          ) : job.status === 'confirmed' ? (
                            <>
                              <Button 
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white gap-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateStatus(job.booking_id, 'in_progress');
                                }}
                              >
                                <Briefcase className="w-4 h-4" />
                                Start Work
                              </Button>
                              <Button 
                                variant="outline" 
                                className="flex-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                View Details
                              </Button>
                            </>
                          ) : job.status === 'in_progress' ? (
                            <>
                              <Button 
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Camera className="w-4 h-4" />
                                Complete Work
                              </Button>
                              <Button 
                                variant="outline" 
                                className="flex-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                View Details
                              </Button>
                            </>
                          ) : (
                            <Button 
                              variant="outline" 
                              className="flex-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View Details
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              {/* Service Area Alert */}
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-base">✓ Service Area</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-green-800">
                  <p>You have active availability in your configured service areas.</p>
                  <Button variant="outline" className="mt-3 w-full text-xs">
                    Adjust Service Area
                  </Button>
                </CardContent>
              </Card>

              {/* Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Quick Stats</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending Responses</span>
                    <span className="font-bold text-yellow-600">{jobs.filter(j => j.status === 'pending').length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Accepted Jobs</span>
                    <span className="font-bold text-green-600">{jobs.filter(j => j.status === 'accepted').length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">In Progress</span>
                    <span className="font-bold text-blue-600">{jobs.filter(j => j.status === 'in_progress').length}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Pro Tips */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">💡 Pro Tips</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-gray-600">
                  <p>• Accept jobs quickly - respond within SLA window</p>
                  <p>• Complete jobs on time to boost your rating</p>
                  <p>• Take quality photos after completion</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </ProviderLayout>
  );
};

export default ProviderAvailableJobsPage;
